<template>
  <div class="w-full">
    <!-- Header -->
    <PageHeader
      title="Produits Prestation"
      subtitle="Services et produits eFashion pour grossistes"
    />

    <div class="w-full px-6 py-8">
      <!-- Banner d'information -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <div class="text-blue-800 text-sm leading-relaxed">
          <p class="mb-2">
            <strong>Bienvenue sur la nouvelle page d'achat de produits et service Efashion</strong> exclusivement réservée aux grossistes
          </p>
          <p class="mb-2">
            Merci de trouver ci-dessous la liste des produits et services proposés. Pour commander, il suffit d'ajouter au panier et de valider pour être livré dans la journée ou le lendemain directement par nos chauffeurs.
          </p>
          <p class="mb-2">
            Les commandes seront automatiquement comptabilisées dans les prestations figurant dans votre facture de commission mensuelle.
          </p>
          <p class="font-semibold">
            Tous les prix affichés sont hors taxes.
          </p>
        </div>
      </div>

      <!-- Layout principal -->
      <div class="space-y-8">
        <!-- Section principale - Catalogue des produits -->
        <div class="w-full">
          <!-- Compteur de produits -->
          <div class="flex items-center justify-between mb-8">
            <h2 class="text-lg font-semibold text-black">
              {{ filteredProducts.length }} Produits
            </h2>
            
            <!-- Filtres -->
            <div class="flex items-center gap-4">
              <BaseInput
                v-model.trim="searchQuery"
                placeholder="Rechercher un produit..."
                class="pt-6 w-64"
              />
            </div>
          </div>

          <!-- Indicateur de chargement -->
          <div v-if="loading" class="text-center py-12">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p class="mt-4 text-black">Chargement des prestations...</p>
          </div>

          <!-- Message si aucun produit -->
          <div v-else-if="filteredProducts.length === 0" class="text-center py-12">
            <div class="bg-gray-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i class="fas fa-box-open text-3xl text-black"></i>
            </div>
            <p class="text-black text-lg">Aucune prestation disponible</p>
            <p class="text-black text-sm mt-2">Veuillez réessayer plus tard</p>
          </div>

          <!-- Grille des produits -->
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              v-for="product in filteredProducts" 
              :key="product.id"
              class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:-translate-y-1 relative pb-16"
              @click="addToCart(product)"
            >
              <div class="mb-4 h-64 rounded-lg overflow-hidden border border-gray-200 bg-gray-200 flex items-center justify-center">
                <img
                  v-if="product.image"
                  :src="product.image"
                  :alt="product.name"
                  class="block w-full h-full object-cover object-center"
                  loading="lazy"
                />
                <i v-else class="fas fa-image text-4xl text-gray-400"></i>
              </div>
              <!-- En-tête du produit -->
              <div class="mb-6">
                <h3 class="text-lg font-bold text-black mb-3">
                  {{ product.name }}
                </h3>
                <p class="text-sm text-black leading-relaxed">
                  {{ product.description }}
                </p>
              </div>

              <!-- Prix en bas à gauche aligné avec le bouton Ajouter (bas droite) -->
              <span class="text-2xl font-bold text-green-600 absolute bottom-6 left-6">
                {{ product.price.toFixed(2) }}€
              </span>

              <!-- Bouton Ajouter fixé en bas à droite -->
              <button 
                class="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 text-sm font-medium shadow-md hover:shadow-lg transform hover:scale-105 absolute bottom-6 right-6"
                @click.stop="addToCart(product)"
              >
                <i class="fas fa-cart-plus mr-2"></i>
                Ajouter
              </button>
            </div>
          </div>
        </div>

        <!-- Section panier en bas -->
        <div class="bg-white border border-gray-200 rounded-xl p-8 shadow-lg">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-xl font-bold text-black">
              <i class="fas fa-shopping-cart mr-3 text-blue-600"></i>
              Mon Panier
            </h3>
            <span class="text-sm text-black">
              {{ cartStore.itemCount }} article{{ cartStore.itemCount > 1 ? 's' : '' }}
            </span>
          </div>
          
          <!-- Ligne de séparation -->
          <div class="border-b border-gray-200 mb-6"></div>

          <!-- Contenu du panier -->
          <div v-if="cartStore.isEmpty" class="text-center py-12">
            <div class="bg-gray-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i class="fas fa-shopping-cart text-3xl text-black"></i>
            </div>
            <p class="text-black text-lg">Votre panier est vide</p>
            <p class="text-black text-sm mt-2">Ajoutez des produits pour commencer</p>
          </div>

          <div v-else class="space-y-4">
            <div 
              v-for="item in cartStore.items" 
              :key="item.id"
              class="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200"
            >
              <div class="flex-1">
                <h4 class="font-semibold text-black text-sm">{{ item.name }}</h4>
                <p class="text-sm text-black">{{ item.price.toFixed(2) }}€ HT</p>
              </div>
              
              <!-- Contrôles de quantité -->
              <div class="flex items-center space-x-3">
                <div class="flex items-center border border-gray-300 rounded-lg">
                  <button 
                    @click="cartStore.setQuantity(item.id, item.quantity - 1)"
                    class="px-3 py-1 text-black hover:text-black hover:bg-gray-100 transition-colors"
                    :disabled="item.quantity <= 1"
                  >
                    <i class="fas fa-minus text-xs"></i>
                  </button>
                  
                  <input 
                    :value="item.quantity"
                    @change="cartStore.setQuantity(item.id, parseInt(($event.target as HTMLInputElement)?.value) || 1)"
                    @blur="cartStore.setQuantity(item.id, parseInt(($event.target as HTMLInputElement)?.value) || 1)"
                    class="w-12 text-center py-1 border-0 focus:ring-0 text-sm font-medium"
                    type="number"
                    min="1"
                    max="99"
                  />
                  
                  <button 
                    @click="cartStore.setQuantity(item.id, item.quantity + 1)"
                    class="px-3 py-1 text-black hover:text-black hover:bg-gray-100 transition-colors"
                  >
                    <i class="fas fa-plus text-xs"></i>
                  </button>
                </div>
                
                <div class="text-right min-w-[80px]">
                  <p class="text-sm font-semibold text-black">
                    {{ (item.price * item.quantity).toFixed(2) }}€
                  </p>
                </div>
                
                <button 
                  @click="removeFromCart(item.id)"
                  class="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors ml-2"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>

            <!-- Total -->
            <div class="border-t-2 border-gray-200 pt-6 mt-6">
              <div class="flex justify-between items-center mb-6">
                <span class="text-lg font-semibold text-black">Total HT:</span>
                <span class="text-2xl font-bold text-green-600">{{ cartTotal.toFixed(2) }}€</span>
              </div>
              
              <button 
                @click="validateOrder"
                class="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <i class="fas fa-check mr-2"></i>
                Valider la commande
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Section historique des commandes -->
      <div class="mt-12">
        <h2 class="text-xl font-semibold text-black mb-6">
          Historique des produits commandés
        </h2>
        
        <div v-if="orders.length === 0" class="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div class="bg-gray-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <i class="fas fa-shopping-bag text-3xl text-black"></i>
          </div>
          <p class="text-black text-lg">Aucune commande trouvée</p>
          <p class="text-black text-sm mt-2">Vos commandes de prestations apparaîtront ici</p>
        </div>
        
        <div v-else class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <ReusableTable
            :columns="orderColumns"
            :data="orders"
            :sortable="true"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de confirmation -->
  <ModalComp
    v-model:isVisible="showConfirmModal"
    title="Confirmer la commande"
    alert="Êtes-vous sûr de vouloir valider cette commande ?"
    cancelText="Annuler"
    confirmText="Confirmer"
    size="sm"
    @confirm="confirmOrder"
  >
    <template #body>
      <!-- Résumé de la commande -->
      <div class="bg-gray-50 rounded-lg p-4 mb-4">
        <div class="flex justify-between items-center mb-2">
          <span class="text-sm text-black">Articles :</span>
          <span class="font-semibold">{{ cartStore.itemCount }}</span>
        </div>
        <div class="flex justify-between items-center">
          <span class="text-sm text-black">Total :</span>
          <span class="font-bold text-lg text-blue-600">{{ cartTotal.toFixed(2) }}€ HT</span>
        </div>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { 
  PageHeader, 
  BaseInput, 
  ReusableTable,
  ModalComp
} from '@/components'
import type { 
  PrestationProduct, 
  PrestationOrder, 
  PrestationApiResponse,
  VendeurPrestationApiResponse
} from '@/types/prestation'
import { useCartStore } from '@/stores/cart'
import { useAuthStore } from '@/stores/auth'
import { prestationsApi, commandesPrestationsApi } from '@/api'
import { getApiBaseUrl } from '@/config/api'
import { useNotification } from '@/composables/useNotification'
import {useRouter} from "vue-router";

// Stores
const cartStore = useCartStore()
const authStore = useAuthStore()
const router = useRouter()
const { success, error } = useNotification()

// État réactif
const products = ref<PrestationProduct[]>([])
const orders = ref<PrestationOrder[]>([])
const loading = ref(false)
const showConfirmModal = ref(false)

const searchQuery = ref('')
const logoMenuImage = new URL('../../assets/images/Logo menu.jpg', import.meta.url).href
const sacImage = new URL('../../assets/images/Sac.jpg', import.meta.url).href
const bannerImage = new URL('../../assets/images/Banner.jpg', import.meta.url).href
const simpleImage = new URL('../../assets/images/simple.jpg', import.meta.url).href
const marqueADecouvrirImage = new URL('../../assets/images/marque-a-decouvrir.png', import.meta.url).href
const marqueEnVedetteImage = new URL('../../assets/images/marque-en-vedette.png', import.meta.url).href

// Colonnes pour le tableau des commandes
const orderColumns = [
  { key: 'date', title: 'Date', sortable: true, align: 'left' as const },
  { key: 'status', title: 'Statut', sortable: true, align: 'left' as const },
  { key: 'product', title: 'Produits', sortable: true, align: 'left' as const },
  { key: 'quantity', title: 'Quantité', sortable: true, align: 'right' as const },
  { key: 'totalPrice', title: 'Prix Total (€)', sortable: true, align: 'right' as const }
]

// Computed properties
const filteredProducts = computed(() => {
  if (!searchQuery.value) {
    return products.value
  }
  
  return products.value.filter(product =>
    product.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    product.description.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const cartTotal = computed(() => cartStore.total)

const getPrestationImageByName = (name: string): string | null => {
  const normalized = String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()

  if (normalized === 'marque a decouvrir') return marqueADecouvrirImage
  if (normalized === 'marque en vedette') return marqueEnVedetteImage
  if (normalized.includes('logo menu') || (normalized.includes('logo') && normalized.includes('menu'))) {
    return logoMenuImage
  }
  if (normalized.includes('sac')) return sacImage
  if (normalized.includes('banner') || normalized.includes('bannier') || normalized.includes('banniere')) {
    return bannerImage
  }
  if (normalized.includes('push email simple') || (normalized.includes('push') && normalized.includes('simple'))) {
    return simpleImage
  }
  return null
}

const getStatusLabel = (statut: number): 'En panier' | 'En attente' | 'Validé' | 'Annulé' | 'Inconnu' => {
  switch (statut) {
    case 0:
      return 'En panier'
    case 1:
      return 'En attente'
    case 2:
      return 'Validé'
    case 3:
      return 'Annulé'
    default:
      return 'Inconnu'
  }
}


// Méthodes
const loadData = async () => {
  loading.value = true
  try {
    // Vérifier l'authentification
    if (!authStore.isAuthenticated) {
      await authStore.checkAuth()
    }
    
    const prestationsData = await prestationsApi.getVisible()
    
    products.value = prestationsData.map((prestation: PrestationApiResponse) => ({
      id: parseInt(prestation.id_prestation.toString()),
      name: prestation.libelle,
      description: prestation.description || '',
      price: parseFloat(prestation.montant),
      image: prestation.imageUrl
        ? `${getApiBaseUrl()}${prestation.imageUrl}`
        : getPrestationImageByName(prestation.libelle) || undefined,
    }))
    
    if (!authStore.user?.id) {
      orders.value = []
      return
    }
    
    try {
      const commandesData = await commandesPrestationsApi.getCommandes(authStore.user.id)
      
      // Transformer les données API vers le format attendu
      orders.value = commandesData.map((commande: VendeurPrestationApiResponse) => ({
        id: commande.id_vendeur_prestation,
        date: commande.date_creation,
        status: getStatusLabel(commande.id_prestation_statut),
        product: commande.prestation?.libelle || commande.libelle,
        quantity: commande.quantite,
        totalPrice: parseFloat(commande.montant)
      }))
    } catch (err) {
      error('Erreur lors du chargement de l\'historique des commandes')
      orders.value = []
    }
    
  } catch (err) {
    products.value = []
  } finally {
    loading.value = false
  }
}

const addToCart = (product: PrestationProduct) => {
  cartStore.addItem(product)
}

const removeFromCart = (productId: number) => {
  cartStore.removeItem(productId)
}

const validateOrder = () => {
  showConfirmModal.value = true
}

const confirmOrder = async () => {
  if (!authStore.user?.id) {
    error('Erreur : Aucun vendeur connecté')
    return
  }

  if (cartStore.isEmpty) {
    error('Votre panier est vide')
    return
  }

  try {
    // Préparer les articles du panier pour l'envoi en DB
    const articles = cartStore.items.map(item => ({
      id_prestation: parseInt(item.id.toString()),
      quantite: item.quantity,
      montant: item.price * item.quantity,
      libelle: item.name
    }))

    await commandesPrestationsApi.creerCommande(authStore.user.id, articles)
    
    success(`Commande validée pour un total de ${cartTotal.value.toFixed(2)}€ HT`)
    
    cartStore.clearCart()
    
    await loadData()
    
  } catch (err) {
    error('Erreur lors de la validation de la commande. Veuillez réessayer.')
  }
}

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  await loadData()
})
</script>
